import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Group, 
  Text, 
  Stack, 
  Button, 
  Avatar, 
  Modal, 
  LoadingOverlay,
  Collapse,
  Grid
} from '@mantine/core';
import { IconPlus, IconEdit } from '@tabler/icons-react';
import EmployeeForm from '../forms/EmployeeForm';
import './StaffManagementSettings.css';
import { useSalon } from '../../context/SalonContext';

function StaffManagement() {
  const { businessInfo, employees, setEmployees, currentUser } = useSalon();
  const [isLoading, setIsLoading] = useState(true);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    if (businessInfo && employees && currentUser) {
      setIsLoading(false);
    }
  }, [businessInfo, employees, currentUser]);

  const handleSave = async (updatedEmployee) => {
    try {
      setEmployees(employees.map(emp => 
        emp.id === selectedEmployee.id ? { ...emp, ...updatedEmployee } : emp
      ));
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };

  const handleEmployeeClick = (employee) => {
    setExpandedEmployee(expandedEmployee?.id === employee.id ? null : employee);
  };

  const handleEditClick = (e, employee) => {
    e.stopPropagation(); // Prevent expanding/collapsing when clicking edit
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleAddStaff = async (newEmployee) => {
    try {
      setEmployees([...employees, newEmployee]);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error creating employee:', error);
    }
  };

  const formatWorkingHours = (employee) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days.map(day => {
      const startTime = employee[`${day}_start_time`];
      const endTime = employee[`${day}_end_time`];
      return {
        day: day.charAt(0).toUpperCase() + day.slice(1),
        hours: startTime && endTime ? `${startTime} - ${endTime}` : 'Unavailable'
      };
    });
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Stack p="lg">
        <Stack spacing="xs">
          {employees && employees.map(employee => (
            <Paper 
              key={employee.id} 
              withBorder 
              onClick={() => handleEmployeeClick(employee)}
              p="md"
              radius="md"
              className={`staff-item ${expandedEmployee?.id === employee.id ? 'selected' : ''}`}
              >
                <Group justify="space-between">
                  <Group>
                    <Avatar 
                      size="md" 
                      radius="xl" 
                      color="violet"
                    >
                      {employee.first_name[0]}
                    </Avatar>
                    <div>
                      <Text size="sm" weight={500}>
                        {employee.first_name} {employee.last_name}
                      </Text>
                      <Text size="xs" color="dimmed">
                        {employee.role}, {employee.title}
                      </Text>
                    </div>
                  </Group>
                  { (employee?.role !== 'Owner' || currentUser?.role === "Owner") && <Button
                    variant="subtle"
                    color="violet"
                    size="xs"
                    leftSection={<IconEdit size="1rem" />}
                    onClick={(e) => handleEditClick(e, employee)}
                  >
                    Edit
                  </Button>}
                </Group>

                <Collapse in={expandedEmployee?.id === employee.id}>
                  <Stack gap="lg" mt="md" pl="xs" py="sm">
                      <Group grow align="flex-start">
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>First Name</Text>
                          <Text size="sm">{employee.first_name}</Text>
                        </Stack>
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>Last Name</Text>
                          <Text size="sm">{employee.last_name}</Text>
                        </Stack>
                      </Group>

                      <Group grow align="flex-start">
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>Title</Text>
                          <Text size="sm">{employee.title}</Text>
                        </Stack>
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>Role</Text>
                          <Text size="sm">{employee.role}</Text>
                        </Stack>
                      </Group>

                      <Group grow align="flex-start">
                        <Stack gap={0}>
                          <Text size="sm" fw={600}>Email</Text>
                          <Text size="sm">{employee.email}</Text>
                        </Stack>
                          <Stack gap={0}>
                          <Text size="sm" fw={600}>Phone</Text>
                          { employee.phone_number ? (
                            <Text size="sm">({employee.area_code}) {employee.phone_number.slice(0, 3)}-{employee.phone_number.slice(3, 7)}</Text>
                          ) : (
                            <Text size="sm" c="dimmed">No phone number provided</Text>
                          )}
                        </Stack>
                      </Group>

                      <Stack gap="xs">
                        <Text size="sm" fw={600}>Working Hours</Text>
                        {formatWorkingHours(employee).map(({ day, hours }) => (
                          <Grid key={day} align="center">
                            <Grid.Col span={4}>
                              <Text size="sm" fw={500}>{day}</Text>
                            </Grid.Col>
                            <Grid.Col span={8}>
                              <Text size="sm" c={hours === 'Unavailable' ? 'dimmed' : 'inherit'}>
                                {hours}
                              </Text>
                            </Grid.Col>
                          </Grid>
                        ))}
                      </Stack>
                  </Stack>
                </Collapse>
            </Paper>
          ))}
        </Stack>

        { currentUser && (currentUser?.role === "Owner" || currentUser?.role === "Manager") && 
          <Button
            fullWidth
            leftSection={<IconPlus size="1rem" />}
            color="violet"
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Staff Member
          </Button>
          }
      </Stack>

      <Modal
        opened={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        size="lg"
        title="Add New Staff Member"
      >
        <EmployeeForm
          onSubmit={handleAddStaff}
          onClose={() => setIsAddModalOpen(false)}
        />
      </Modal>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        size="lg"
        title="Edit Staff Member"
      >
        <EmployeeForm
          initialData={selectedEmployee}
          onSubmit={handleSave}
          onClose={() => setIsEditModalOpen(false)}
          showOnlyHours={false}
        />
      </Modal>
    </>
  );
}

export default StaffManagement; 